import React, { useCallback, useEffect, useState, useRef } from "react";
import JumboContentLayout from "@jumbo/components/JumboContentLayout";
import HeaderPages from "app/pages/components/HeaderPages";
import { 
  Grid, 
  Button, 
  Dialog, 
  DialogContent, 
  Typography, 
  CircularProgress, 
  LinearProgress, 
  Box, 
  Stepper, 
  Step, 
  StepLabel,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  Stack,
  Chip
} from "@mui/material";
import ProcessDetailsHeader from "app/pages/components/ProcessDetailsHeader";
import "moment/locale/pt";

import ProcessMovements from "app/pages/components/ProcessMovements";
import { ProcessBrandDetails } from "app/pages/components/ProcessBrandDetails/ProcessBrandDetails";
import PersonIcon from "@mui/icons-material/Person";
import PlaceIcon from "@mui/icons-material/Place";
import InfoIcon from "@mui/icons-material/Info";
import GavelIcon from "@mui/icons-material/Gavel";
import { useParams } from "react-router-dom";
import { useAPI } from "app/hooks/useApi";
import Loading from "app/pages/components/Loading";
import ProcessDispatches from "app/pages/components/ProcessDispatches";
import { ProcessDeadlines } from "app/pages/components/ProcessDeadlines/ProcessDeadlines";
import { ProcessPetitions } from "app/pages/components/ProcessPetitions/ProcessPetitions";
import { ProcessPublications } from "app/pages/components/ProcessosPublicacoes/ProcessosPublicacoes";
import FileUpload from "app/pages/components/FileUpload/FileUpload";
import ProcessosAnexos from "app/pages/components/ProcessosAnexos/ProcessosAnexos";
import ProcessosOcorrencias from "app/pages/components/ProcessosOcorrencias";
import ProcessProvidencias from "app/pages/components/ProcessProvidencias/ProcessProvidencias";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { mapApiError } from "app/utils/errorMessages";
import ScrapingStatusBadge from "app/pages/components/ScrapingStatusBadge";
import { needsUpdate, formatScrapingDate, daysSince } from "app/utils/scrapingUtils";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const DetalhesMarcaProcesso = () => {
  const [processo, setProcesso] = useState({});
  const [cabecalhosProcesso, setCabecalhosProcesso] = useState([]);
  const [statusExtracaoDados, setStatusExtracaoDados] = useState(null);
  const [occurrences, setOccurrences] = useState([]);
  const [providencias, setProvidencias] = useState([]);
  const [atualizando, setAtualizando] = useState(false);
  const [waitingUpdate, setWaitingUpdate] = useState(false);
  const [waitingMessage, setWaitingMessage] = useState('');
  const [updateProgress, setUpdateProgress] = useState(0);
  const [updateStep, setUpdateStep] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [backgroundUpdate, setBackgroundUpdate] = useState(false);
  const [showBackgroundSuccess, setShowBackgroundSuccess] = useState(false);
  const pollIntervalRef = useRef(null);
  const pollAttemptsRef = useRef(0);
  const timeIntervalRef = useRef(null);
  const backgroundUpdateRef = useRef(false);
  const MAX_POLL_ATTEMPTS = 60; // up to 2 minutes if interval is 2000ms
  const POLL_INTERVAL_MS = 2000;
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [pageError, setPageError] = useState('');
  
  const updateSteps = [
    'Solicitando atualização',
    'Processando dados',
    'Atualizando banco de dados',
    'Finalizando'
  ];
  
  const { makeRequest, loading } = useAPI();

  const { id } = useParams();
  const fetchProvidenciasRef = useRef(false);

  const getProcesso = useCallback(async () => {
    try {
      const response = await makeRequest(`processos-revistas-rpi/${id}`, "GET");
      if (response.status === 200) {
        setProcesso(response.data);
        setPageError('');
      } else {
        setPageError(mapApiError(response));
      }
    } catch (err) {
      setPageError(mapApiError(err));
    }
  }, [id, makeRequest]);

  const getProcessoOccurrences = useCallback(async () => {
    if (!id) {
      return;
    }
    try {
      const url = `processos-revistas-rpi/processos/${id}/ocorrencias`;
      const response = await makeRequest(url, 'GET');
      let ocorrencias = [];
      if (response.status === 200) {
        if (Array.isArray(response.data)) {
          ocorrencias = response.data;
        } else if (Array.isArray(response)) {
          ocorrencias = response;
        } else if (response.data && typeof response.data === 'object' && Array.isArray(response.data.data)) {
          ocorrencias = response.data.data;
        }
      }
      setOccurrences(ocorrencias);
    } catch (err) {
  setOccurrences([]);
  setSnackbar({ open: true, severity: 'error', message: mapApiError(err) });
    }
  }, [id]);

  const getProcessoProvidencias = useCallback(async () => {
    if (!id || fetchProvidenciasRef.current) return;
    
    fetchProvidenciasRef.current = true;
    try {
      const response = await makeRequest(`agenda/processo/${id}`, "GET");
      if (response.status === 200) {
        setProvidencias(response.data);
      }
    } catch (error) {

  setSnackbar({ open: true, severity: 'error', message: mapApiError(error) });
    }
  }, [id]);

  const handleDeleteAttachment = async (attachment) => {
    if (!attachment?.id) return;
    try {
      const resp = await makeRequest(`processos-revistas-rpi/anexos/${attachment.id}`, 'DELETE');
      if (resp && (resp.status === 200 || resp.status === 204)) {
        setProcesso(prev => ({
          ...prev,
          anexos_processos: (prev.anexos_processos || []).filter(a => a.id !== attachment.id)
        }));
        setSnackbar({ open: true, severity: 'success', message: 'Anexo removido com sucesso.' });
      } else {
        setSnackbar({ open: true, severity: 'error', message: mapApiError(resp) });
      }
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: mapApiError(err) });
    }
  };

  const handleAddOccurrence = (occurrence) => {
    setOccurrences((prev) => [occurrence, ...prev]);
  };

  const handleEditOccurrence = async (occurrenceId, updatedFields) => {
    try {
      const url = `http://localhost:3001/processos-revistas-rpi/processos/${id}/ocorrencias/${occurrenceId}`;
      const payload = {
        data: updatedFields.data,
        descricao: updatedFields.descricao,
        protocolo: updatedFields.protocolo,
        detalhes: updatedFields.detalhes
      };
      const response = await makeRequest(url, 'PUT', payload);
      if (response.status === 200) {
        if (typeof getProcessoOccurrences === 'function') {
          await getProcessoOccurrences();
        }
        setSnackbar({ open: true, severity: 'success', message: 'Ocorrência atualizada.' });
      }
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: mapApiError(err) });
    }
  };

  const refetchProvidencias = useCallback(async () => {
    if (!id) return;
    
    try {
      const response = await makeRequest(`agenda/processo/${id}`, "GET");
      if (response.status === 200) {
        setProvidencias(response.data);
      }
    } catch (error) {

  setSnackbar({ open: true, severity: 'error', message: mapApiError(error) });
    }
  }, [id, makeRequest]);

  // Função para fechar modal mantendo atualização em background
  const handleCloseModalKeepUpdating = () => {
    setWaitingUpdate(false);
    setBackgroundUpdate(true);
    // Mantém os intervals rodando em background
  };

  // Função para chamar a rota de atualização
  const handleAtualizarProcesso = async () => {
    // iniciar fluxo de atualização + modal de espera
    let processResponse = null;
    
    try {
      // SEMPRE mostrar o modal primeiro
      setWaitingUpdate(true);
      setBackgroundUpdate(false);
      setUpdateStep(0);
      setUpdateProgress(0);
      setTimeElapsed(0);
      setWaitingMessage('Verificando dados do processo...');

      // Iniciar contador de tempo
      timeIntervalRef.current = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);

      // Validação após mostrar o modal
      if (!processo?.numero && !id) {
        setWaitingMessage('Erro: Dados do processo não encontrados.');
        clearInterval(timeIntervalRef.current);
        setTimeout(() => {
          setWaitingUpdate(false);
          setUpdateProgress(0);
          setUpdateStep(0);
          setTimeElapsed(0);
        }, 3000);
        return;
      }

      // Se não tem número mas tem ID, tenta buscar o processo primeiro
      if (!processo?.numero && id) {
        setWaitingMessage('Carregando dados do processo...');
        try {
          processResponse = await makeRequest(`processos-revistas-rpi/${id}`, "GET");
          if (processResponse.status === 200 && processResponse.data?.numero) {
            setProcesso(processResponse.data);
            // Continua com o número obtido
          } else {
            setWaitingMessage('Erro: Não foi possível obter o número do processo.');
            clearInterval(timeIntervalRef.current);
            setTimeout(() => {
              setWaitingUpdate(false);
              setUpdateProgress(0);
              setUpdateStep(0);
              setTimeElapsed(0);
            }, 3000);
            return;
          }
        } catch (error) {
          setWaitingMessage('Erro ao carregar dados do processo.');
          clearInterval(timeIntervalRef.current);
          setTimeout(() => {
            setWaitingUpdate(false);
            setUpdateProgress(0);
            setUpdateStep(0);
            setTimeElapsed(0);
          }, 3000);
          return;
        }
      }

      const numeroProcesso = processo?.numero || (processResponse?.data?.numero);

      // 1) dispara o scrap no backend
      setUpdateStep(0);
      setUpdateProgress(25);
      setWaitingMessage('Solicitando atualização ao servidor...');
      
      const resp = await makeRequest(`/scrap/processo/${numeroProcesso}`, 'GET');

      if (!resp || resp.status !== 200) {
        const msg = 'O Sistema do INPI enfrenta avarias, tente novamente ou busque um administrador do sistema';
        setWaitingMessage(msg);
        setSnackbar({ open: true, severity: 'error', message: msg });
        clearInterval(timeIntervalRef.current);
        // mantém o modal aberto por um curto período para exibir erro, então fecha
        setTimeout(() => setWaitingUpdate(false), 5000);
        return;
      }

      // 2) começa a aguardar a atualização no banco fazendo polling no endpoint do processo
      setUpdateStep(1);
      setUpdateProgress(50);
      setWaitingMessage('Processando dados no servidor...');
      
      // Simular progresso durante o processamento
      setTimeout(() => {
        setUpdateStep(2);
        setUpdateProgress(75);
        setWaitingMessage('Aguardando atualização no banco de dados...');
      }, 2000);

      const initialSnapshot = JSON.stringify(processo || {});
      pollAttemptsRef.current = 0;

      pollIntervalRef.current = setInterval(async () => {
        pollAttemptsRef.current += 1;
        try {
          const r = await makeRequest(`processos-revistas-rpi/${id}`, 'GET');
          if (r && r.status === 200) {
            if (r.data?.is_scraping_completo === true) {
              // detectou que scraping foi concluído
              setUpdateStep(3);
              setUpdateProgress(100);
              setWaitingMessage('Atualização concluída!');
              setProcesso(r.data);
            
              clearInterval(pollIntervalRef.current);
              clearInterval(timeIntervalRef.current);
              pollIntervalRef.current = null;
              timeIntervalRef.current = null;
              backgroundUpdateRef.current = false;
              setBackgroundUpdate(false);
            
              if (!backgroundUpdateRef.current) {
                setTimeout(() => {
                  setWaitingUpdate(false);
                  setUpdateProgress(0);
                  setUpdateStep(0);
                  setTimeElapsed(0);
                }, 1000);
              } else {
                setShowBackgroundSuccess(true);
                setTimeout(() => setShowBackgroundSuccess(false), 5000);
              }
            } else if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
              clearInterval(pollIntervalRef.current);
              clearInterval(timeIntervalRef.current);
              pollIntervalRef.current = null;
              timeIntervalRef.current = null;
              
              const msg = 'O INPI demorou mais que o esperado. Tente novamente em alguns minutos.';
              setWaitingMessage(msg);
              setSnackbar({ open: true, severity: 'warning', message: msg });
              
              setTimeout(() => {
                setWaitingUpdate(false);
                setUpdateProgress(0);
                setUpdateStep(0);
                setTimeElapsed(0);
              }, 3000);
            }
          }
        } catch (err) {
          // erro temporário no polling: registra e continua até timeout

          if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
            clearInterval(pollIntervalRef.current);
            clearInterval(timeIntervalRef.current);
            pollIntervalRef.current = null;
            const msg = 'Erro ao verificar atualização.';
            setWaitingMessage(msg);
            setSnackbar({ open: true, severity: 'error', message: msg });
            setTimeout(() => setWaitingUpdate(false), 2000);
          }
        }
      }, POLL_INTERVAL_MS);
    } catch (error) {

      const msg = 'O Sistema do INPI enfrenta avarias, tente novamente ou busque um administrador do sistema';
      setWaitingMessage(msg);
      setSnackbar({ open: true, severity: 'error', message: mapApiError(error) });
      clearInterval(timeIntervalRef.current);
      setTimeout(() => setWaitingUpdate(false), 5000);
    }
  };

  useEffect(() => {
    getProcesso();
  }, [id]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
        timeIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    backgroundUpdateRef.current = backgroundUpdate;
  }, [backgroundUpdate]);

  useEffect(() => {
    getProcessoOccurrences();
  }, [id]);

  useEffect(() => {
    getProcessoProvidencias();
  }, [id]);

  useEffect(() => {
    if (!processo) return;
    if (processo) {
      const titularesHeaders = processo?.titulares
        ?.map((titular, index) => [
          {
            title:
              processo.titulares.length === 1
                ? "Titular"
                : `Titular (${index + 1})`,
            subtitle: titular.nome_razao_social || "- - -",
            icon: <PersonIcon />,
          },
        ])
        ?.flat();
      
      const cabecalhos = [
        {
          title: "Número",
          subtitle: processo?.numero,
        },
        {
          title: "Situação",
          subtitle: processo?.situacao ?? "- - -",
          icon: <InfoIcon />,
        },
        {
          title: "Procurador",
          subtitle: processo?.procurador ?? "- - -",
          icon: <GavelIcon />,
        },
        ...(titularesHeaders || []),
        {
          title: "Local",
          subtitle:
            processo?.titulares?.length > 0 && (processo.titulares[0].uf || processo.titulares[0].pais)
              ? `${processo.titulares[0].uf || '---'} - ${processo.titulares[0].pais || '---'}`
              : '---',
          icon: <PlaceIcon />,
        },
        {
          title: "Responsável",
          subtitle: processo?.responsavel
            ? `${processo?.responsavel?.name}`
            : "- - -",
          icon: <PersonIcon />,
        },
        {
          title: "Envolvido",
          subtitle:
            processo?.envolvido?.length > 0
              ? `${processo?.envolvido[0]?.envolvido?.nome_identificacao}`
              : "- - -",
          icon: <PersonIcon />,
        },
      ];
      
      setCabecalhosProcesso(cabecalhos);
    }
  }, [processo]);

  // Comentando até resolver o SSE no servidor
  // useEffect(() => {
  //   if (!id) return;

  //   const eventSource = new EventSource(`${process.env.REACT_APP_BACKEND_URL}/processos-revistas-rpi/sse/${id}`);

  //   eventSource.onmessage = (event) => {
  //     const status = event.data;
  //     console.log("Status do SSE:", status);

  //     setStatusExtracaoDados(status);

  //     if (status === "done" || status === "completed") {
  //       eventSource.close();
  //     }
  //   };

  //   eventSource.onerror = (err) => {
  //     console.error("Erro no SSE:", err);
  //     eventSource.close();
  //   };

  //   return () => {
  //     eventSource.close();
  //   };
  // }, [id]);

  return (
    <>
      {pageError && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{pageError}</Alert>
        </Box>
      )}
      {/* Dialog de loading para atualização */}
      <Dialog open={atualizando} PaperProps={{ style: { background: 'transparent', boxShadow: 'none' } }}>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 250, minHeight: 120, background: '#fff', borderRadius: 8 }}>
          <CircularProgress color="primary" />
          <Typography variant="body1" mt={2}>
            Atualizando dados do processo...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Dialog de espera com progresso avançado */}
      <Dialog 
        open={waitingUpdate} 
        PaperProps={{ 
          style: { 
            background: 'transparent', 
            boxShadow: 'none',
            overflow: 'visible'
          } 
        }} 
        onClose={() => { /* fechado somente programaticamente */ }}
      >
        <DialogContent 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minWidth: 400, 
            minHeight: 300, 
            background: '#fff', 
            borderRadius: 12,
            padding: '32px',
            position: 'relative'
          }}
        >
          {/* Botão de fechar no canto superior direito */}
          <Tooltip title="Fechar modal (atualização continua em segundo plano)" placement="left">
            <IconButton
              onClick={handleCloseModalKeepUpdating}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                backgroundColor: 'rgba(0,0,0,0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.08)',
                }
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* Progresso circular */}
          <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
            <CircularProgress 
              variant="determinate" 
              value={updateProgress} 
              size={80}
              thickness={4}
              color="primary"
              sx={{
                animation: updateProgress < 100 ? 'pulse 2s ease-in-out infinite alternate' : 'none',
                '@keyframes pulse': {
                  '0%': {
                    opacity: 1,
                  },
                  '100%': {
                    opacity: 0.7,
                  },
                },
              }}
            />
            <CircularProgress 
              variant="indeterminate"
              size={80}
              thickness={4}
              color="primary"
              sx={{ 
                position: 'absolute',
                opacity: 0.3,
                display: updateProgress < 100 ? 'block' : 'none'
              }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6" component="div" color="primary" fontWeight="bold">
                {updateProgress === 100 ? '✓' : `${Math.round(updateProgress)}%`}
              </Typography>
            </Box>
          </Box>

          {/* Stepper das etapas */}
          <Box sx={{ width: '100%', mb: 3 }}>
            <Stepper activeStep={updateStep} orientation="vertical">
              {updateSteps.map((label, index) => (
                <Step key={label}>
                  <StepLabel>
                    <Typography variant="body2" color={index <= updateStep ? "primary" : "text.secondary"}>
                      {label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Barra de progresso linear */}
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={updateProgress} 
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          {/* Mensagem principal */}
          <Typography variant="body1" textAlign="center" fontWeight={500} mb={1}>
            {waitingMessage || 'Aguardando atualização...'}
          </Typography>

          {/* Tempo decorrido */}
          <Typography variant="caption" color="text.secondary" textAlign="center">
            Tempo decorrido: {Math.floor(timeElapsed / 60)}:{(timeElapsed % 60).toString().padStart(2, '0')}
          </Typography>
        </DialogContent>
      </Dialog>
      
      {/* Indicador de atualização em background */}
      {backgroundUpdate && (
        <Box
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 1300,
            backgroundColor: 'primary.main',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            boxShadow: 3
          }}
        >
          <CircularProgress size={16} color="inherit" />
          <Typography variant="body2">
            Atualizando processo...
          </Typography>
        </Box>
      )}

      {/* Notificação de sucesso da atualização em background */}
      {showBackgroundSuccess && (
        <Box
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 1300,
            backgroundColor: 'success.main',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            boxShadow: 3
          }}
        >
          <Typography variant="body2">
            ✓ Processo atualizado com sucesso!
          </Typography>
        </Box>
      )}
      
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={handleAtualizarProcesso}
          disabled={atualizando || waitingUpdate}
        >
          {waitingUpdate ? 'Aguardando...' : atualizando ? 'Atualizando...' : 'Atualizar'}
        </Button>
      </Box>
      <JumboContentLayout
        header={
          <HeaderPages
            title={"Detalhes do processo"}
            hasButton={true}
            isBackButton={true}
          />
        }
      >
        {loading ? (
          <Loading />
        ) : (
          <>
            <Grid container width="100%" spacing={2}>
              <Grid item xs={12}>
                <ProcessBrandDetails
                  processNumber={processo?.numero}
                  brandName={
                    processo?.marca?.length > 0 ? processo?.marca[0]?.nome : null
                  }
                  nature={
                    // ✅ CORREÇÃO: Buscar natureza primeiro da tabela principal, depois da relação
                    processo?.natureza || 
                    (processo?.marca?.length > 0 ? processo?.marca[0]?.natureza : null)
                  }
                  presentation={
                    // ✅ CORREÇÃO: Buscar apresentação primeiro da tabela principal, depois da relação
                    processo?.apresentacao || 
                    (processo?.marca?.length > 0 ? processo?.marca[0]?.apresentacao : null)
                  }
                  is_scraping_completo={processo?.is_scraping_completo}
                  fonte_scraping={processo?.fonte_scraping}
                  data_ultimo_scraping={processo?.data_ultimo_scraping}
                />
              </Grid>
              
              {/* Card de Status de Scraping */}
              <Grid item xs={12}>
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Status dos Dados:
                        </Typography>
                        <ScrapingStatusBadge
                          is_scraping_completo={processo?.is_scraping_completo}
                          fonte_scraping={processo?.fonte_scraping}
                          data_ultimo_scraping={processo?.data_ultimo_scraping}
                          size="medium"
                          showTooltip={true}
                        />
                      </Box>
                      
                      <Divider orientation="vertical" flexItem />
                      
                      {processo?.fonte_scraping && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Fonte:
                          </Typography>
                          <Chip 
                            label={processo.fonte_scraping} 
                            size="small" 
                            variant="outlined"
                            color="primary"
                          />
                        </Box>
                      )}
                      
                      {processo?.data_ultimo_scraping && (
                        <>
                          <Divider orientation="vertical" flexItem />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              Última atualização: {formatScrapingDate(processo.data_ultimo_scraping)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ({daysSince(processo.data_ultimo_scraping)} dias atrás)
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Stack>
                    
                    {/* Alerta de dados desatualizados */}
                    {needsUpdate(processo) && (
                      <Alert 
                        severity="warning" 
                        icon={<WarningAmberIcon />}
                        sx={{ mt: 2 }}
                      >
                        <Typography variant="body2">
                          Os dados deste processo podem estar desatualizados. 
                          {processo?.data_ultimo_scraping && 
                            ` Última sincronização há ${daysSince(processo.data_ultimo_scraping)} dias.`
                          }
                          {' '}Recomendamos atualizar os dados clicando no botão "Atualizar" acima.
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={8}>
                <ProcessDetailsHeader
                  data={cabecalhosProcesso}
                  data_concessao={processo?.data_concessao}
                  data_deposito={processo?.data_deposito}
                  data_vigencia={processo?.data_vigencia}
                  data_publicacao={processo?.data_publicacao}
                  data_deferimento={processo?.data_deferimento}
                  data_prorrogacao={processo?.data_prorrogacao}
                  key="cabecalho"
                />
              </Grid>
              <Grid item xs={4}>
                <ProcessMovements process={processo} />
              </Grid>
              <Grid item xs={8}>
                <ProcessDispatches dispatches={processo?.despachos} />
              </Grid>

              {processo?.prazos?.length > 0 &&
              Object.values(processo?.prazos?.[0] || {}).some(
                (value) => value !== null
              ) ? (
                <Grid item xs={12}>
                  <ProcessDeadlines deadlines={processo?.prazos} />
                </Grid>
              ) : null}

              <Grid item xs={8}>
                <ProcessPetitions
                  petitions={processo?.peticoes_processo}
                  processId={id}
                  getProcess={getProcesso}
                />
              </Grid>
              <Grid item xs={12}>
                <ProcessPublications petitions={processo?.peticoes_processo} />
              </Grid>

              <Grid item xs={8}>
                <FileUpload 
                  processId={id}
                  onUploadComplete={getProcesso}
                />
                <ProcessosAnexos 
                  attachments={processo?.anexos_processos || []} 
                  onDelete={handleDeleteAttachment}
                />
              </Grid>
              <Grid item xs={12}>
                <ProcessosOcorrencias
                  occurrences={occurrences}
                  onAddOccurrence={handleAddOccurrence}
                  processId={id}
                  onSyncOccurrences={getProcessoOccurrences}
                  onEditOccurrence={handleEditOccurrence}
                />
              </Grid>
              <Grid item xs={8}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Button
                    variant="outlined"
                    color="secondary"
                    disabled={atualizando}
                    onClick={async () => {
                      if (!processo?.numero) return;
                      setAtualizando(true);
                      try {
                        const response = await makeRequest('/agenda/criar-por-numero', 'POST', { numero: processo.numero });
                        if (response && response.status === 200) {
                          if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
                            await Promise.all([
                              refetchProvidencias(),
                              getProcesso()
                            ]);
              setSnackbar({ open: true, severity: 'success', message: response.data?.message || 'Agenda atualizada.' });
                          } else {
                            await refetchProvidencias();
              setSnackbar({ open: true, severity: 'success', message: 'Agenda atualizada.' });
                          }
                        }
                      } catch (err) {
            setSnackbar({ open: true, severity: 'error', message: mapApiError(err) });
                      }
                      setAtualizando(false);
                    }}
                  >
                    {atualizando ? 'Atualizando Agenda...' : 'Atualizar Agenda'}
                  </Button>
                </Box>
                <ProcessProvidencias 
                  providencias={providencias} 
                  processId={id}
                  numeroProcesso={processo?.numero}
                  onProvidenciaAdded={refetchProvidencias}
                />
              </Grid>
            </Grid>
          </>
        )}
      </JumboContentLayout>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DetalhesMarcaProcesso;
